import { Injectable, signal, computed } from '@angular/core';
import { User } from '../../shared/model/auth.interface';

/**
 * Servicio encargado de la persistencia y gestión reactiva del estado de autenticación.
 * * Además de sincronizar los datos con `localStorage`, este servicio expone señales (Signals)
 * que permiten a la aplicación reaccionar en tiempo real a cambios en la sesión del usuario
 * (login, logout o actualización de perfil) sin necesidad de refrescar el navegador.
 */
@Injectable({
  providedIn: 'root',
})
export class TokenStorageService {
  /** Clave para el almacenamiento del token JWT. */
  private readonly tokenKey = 'auth_token';
  /** Clave para el almacenamiento del objeto de usuario serializado. */
  private readonly userKey = 'auth_user';

  /** * Signal interno que mantiene el estado actual del usuario.
   * Se inicializa recuperando los datos del almacenamiento persistente.
   */
  private readonly userSignal = signal<User | null>(this.getUserFromStorage());

  /** * Signal de solo lectura expuesto para el consumo de componentes.
   * Proporciona una fuente de verdad reactiva para toda la aplicación.
   */
  readonly user$ = computed(() => this.userSignal());

  /**
   * Actualiza y persiste el token de sesión.
   * * @param token Cadena del token proporcionada por el backend.
   */
  saveToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  /**
   * Recupera el token actual del almacenamiento.
   * * @returns El token almacenado o `null` si no existe sesión activa.
   */
  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  /**
   * Guarda los datos del usuario y notifica a todos los suscriptores reactivos.
   * * @param user Objeto con la información del usuario a persistir.
   */
  saveUser(user: User): void {
    localStorage.setItem(this.userKey, JSON.stringify(user));
    this.userSignal.set(user); // Notifica el cambio en tiempo real
  }

  /**
   * Recupera el usuario actual (método síncrono para casos puntuales).
   * * @returns El objeto `User` actual o `null`.
   */
  getUser(): User | null {
    return this.userSignal();
  }

  /**
   * Lógica interna para reconstruir el usuario desde localStorage al inicializar el servicio.
   * * @returns El usuario parseado o `null` si los datos son inválidos.
   */
  private getUserFromStorage(): User | null {
    const user = localStorage.getItem(this.userKey);
    if (!user) return null;
    try {
      return JSON.parse(user) as User;
    } catch {
      this.clear();
      return null;
    }
  }

  /**
   * Limpia la sesión completa y resetea el estado reactivo a null.
   * Invalida automáticamente todas las vistas que dependan de la información del usuario.
   */
  clear(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
    this.userSignal.set(null); // Notifica el cierre de sesión a la UI
  }
}
